import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CInput,
  CInputGroup,
  CInputGroupAppend,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { deleteItem, getItem } from "../api/generics";
import { SUCCESS } from "../utils/constants/tags";

interface AiConnection {
  id: number;
  name: string | null;
  tokenPrefix: string;
  origin: string;
  clientId: string | null;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

interface AiConnectionsData {
  connections: AiConnection[];
  taxPayers: { id: number; ruc: string; socialName: string }[];
  visible: boolean;
  connectorUrl: string;
}

const formatDate = (value: string | null) =>
  value === null ? "nunca" : new Date(value).toLocaleString("es-PY");

/**
 * Mi cuenta → Conexiones de IA.
 *
 * A screen about the person, not about a company: the credential belongs to
 * whoever holds it and reaches every company they administer, so there is
 * nothing per company to list here and nothing per company to switch off. The
 * per company control is `ai.access`, and it belongs to the owner of each one.
 */
const AiConnectionsPage = () => {
  const [data, setData] = useState<AiConnectionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<AiConnection | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const response = await getItem<AiConnectionsData>("/oauth/connections/");

    if (response.status === SUCCESS && response.data !== undefined) {
      setData(response.data);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = useCallback(async () => {
    if (pending === null) {
      return;
    }

    setRevoking(true);
    await deleteItem(`/oauth/connections/${pending.id}/`, {});
    setPending(null);
    setRevoking(false);
    await load();
  }, [pending, load]);

  const copyConnectorUrl = useCallback(async () => {
    if (data === null) {
      return;
    }

    try {
      await navigator.clipboard.writeText(data.connectorUrl);
    } catch (e) {
      // The clipboard API needs a secure context; plain http falls back to
      // the old way.
      const textarea = document.createElement("textarea");
      textarea.value = data.connectorUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [data]);

  if (loading) {
    return (
      <CContainer fluid className="pt-3">
        <p className="text-muted">Cargando…</p>
      </CContainer>
    );
  }

  const connections = data?.connections ?? [];

  return (
    <CContainer fluid className="pt-3">
      <CCard>
        <CCardHeader>
          <strong>Conexiones de IA</strong>
        </CCardHeader>
        <CCardBody>
          <p className="text-muted">
            Los asistentes de IA que conectaste a tu cuenta de SmartDoc. Cada
            uno consulta con tus permisos, y sólo las empresas que tengan la IA
            habilitada para vos.
          </p>

          {connections.length === 0 ? (
            <p className="mb-0">Todavía no conectaste ningún asistente.</p>
          ) : (
            <>
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="d-flex flex-column flex-sm-row justify-content-between align-items-start border-top py-3"
                >
                  <div>
                    <strong>{connection.name ?? "Asistente de IA"}</strong>{" "}
                    <code className="text-muted small">
                      {connection.tokenPrefix}…
                    </code>
                    <div className="text-muted small">
                      conectado el {formatDate(connection.createdAt)} · último
                      uso {formatDate(connection.lastUsedAt)}
                    </div>
                    {connection.scopes.length > 0 && (
                      <div className="text-muted small">
                        Puede: {connection.scopes.join(". ")}.
                      </div>
                    )}
                  </div>
                  <CButton
                    color="danger"
                    variant="outline"
                    size="sm"
                    className="mt-2 mt-sm-0"
                    onClick={() => setPending(connection)}
                  >
                    Desconectar
                  </CButton>
                </div>
              ))}
              {/* Not a disclaimer: the person has to know this is one switch
                  and not one per company, because the per company control is
                  somewhere else and belongs to somebody else. */}
              <p className="text-muted small mt-3 mb-0">
                Desconectar la quita de todas tus empresas a la vez.
              </p>
            </>
          )}
        </CCardBody>
      </CCard>

      {/* Shown whether or not there is a connection already: somebody with one
          assistant connected is exactly the person who goes looking for how to
          connect a second one. */}
      <CCard>
        <CCardHeader>
          <strong>Cómo conectar un asistente</strong>
        </CCardHeader>
        <CCardBody>
          <ol className="pl-4 mb-0">
            <li>
              En tu asistente, buscá dónde se agregan conectores personalizados:
              <ul className="pl-3 mb-0">
                <li>
                  <strong>Claude:</strong> Configuración → Conectores → Agregar
                  conector personalizado.
                </li>
                <li>
                  <strong>ChatGPT:</strong> primero Configuración → Seguridad e inicio de sesión → activá
                  el modo desarrollador. Después entrá a Plugins, tocá el botón
                  + y, en Conexión, pegá la dirección del conector.
                </li>
              </ul>
            </li>
            <li>
              Pegá la dirección del conector de SmartDoc:
              {data?.connectorUrl !== undefined && (
                <CInputGroup className="my-2">
                  <CInput
                    readOnly
                    value={data.connectorUrl}
                    onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                      event.target.select()
                    }
                  />
                  <CInputGroupAppend>
                    <CButton
                      color={copied ? "success" : "primary"}
                      onClick={copyConnectorUrl}
                    >
                      {copied ? "Copiada" : "Copiar"}
                    </CButton>
                  </CInputGroupAppend>
                </CInputGroup>
              )}
              Si te pregunta el tipo de autenticación, elegí OAuth.
            </li>
            <li>
              Se va a abrir SmartDoc para que inicies sesión y autorices la
              conexión. Después volvés solo a tu asistente.
            </li>
          </ol>
          {/* The menus belong to each provider and they move them around; the
              step that is ours, the SmartDoc screen, is the one that does not
              change. */}
          <p className="text-muted small mt-3 mb-0">
            Si los conectores están disponibles y cómo se agregan depende de
            cada asistente y de tu plan, y puede cambiar con sus
            actualizaciones. Si no encontrás estos menús, buscá en la ayuda de
            tu asistente cómo agregar un conector MCP personalizado.
          </p>
          {/* No password to copy and no key to rotate: whatever the assistant
              needs it gets by itself, through that screen. */}
          <p className="text-muted small mt-3 mb-0">
            No hay ninguna clave que copiar ni que guardar. Si usás el asistente
            desde el celular, no hace falta hacer nada distinto: el conector que
            agregaste aparece también ahí.
          </p>
        </CCardBody>
      </CCard>

      {/* Only with more than one: with a single company there is no question
          of which ones it reaches. */}
      {(data?.taxPayers.length ?? 0) > 1 && (
        <CCard>
          <CCardHeader>
            <strong>A qué empresas alcanza</strong>
          </CCardHeader>
          <CCardBody>
            <ul className="pl-4 mb-2">
              {data?.taxPayers.map((taxPayer) => (
                <li key={taxPayer.id}>
                  {taxPayer.socialName} — {taxPayer.ruc}
                </li>
              ))}
            </ul>
            <p className="text-muted small mb-0">
              Esta lista se calcula en cada consulta. Si te agregan a una
              empresa, aparece sola; si le sacan la IA, desaparece sola.
            </p>
          </CCardBody>
        </CCard>
      )}

      <CModal show={pending !== null} onClose={() => setPending(null)} centered>
        <CModalHeader closeButton>
          <strong>Desconectar el asistente</strong>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            {pending?.name ?? "El asistente"} deja de tener acceso a tu
            facturación de inmediato, en todas tus empresas. Para volver a
            usarlo vas a tener que conectarlo de nuevo.
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            disabled={revoking}
            onClick={() => setPending(null)}
          >
            Cancelar
          </CButton>
          <CButton color="danger" disabled={revoking} onClick={revoke}>
            {revoking ? "Desconectando…" : "Desconectar"}
          </CButton>
        </CModalFooter>
      </CModal>
    </CContainer>
  );
};

export default AiConnectionsPage;
